import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number; // milliseconds, 0 means no auto-dismiss
}

export interface AppError {
  id: string;
  message: string;
  code?: string;
  statusCode?: number;
  details?: unknown;
  timestamp: Date;
}

interface ErrorState {
  // Toast management
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Error management
  errors: AppError[];
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;

  // Convenience methods for common toast types
  showSuccess: (message: string, title?: string, duration?: number) => void;
  showError: (message: string, title?: string, duration?: number) => void;
  showWarning: (message: string, title?: string, duration?: number) => void;
  showInfo: (message: string, title?: string, duration?: number) => void;
}

const DEFAULT_TOAST_DURATION = 5000; // 5 seconds
const TOAST_DEDUP_WINDOW_MS = 3000; // 3-second deduplication window

const generateId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Track recent toast messages for deduplication
const _recentToastMessages = new Map<string, number>();

export const useErrorStore = create<ErrorState>((set) => ({
  // Initial state
  toasts: [],
  errors: [],

  // Toast actions
  addToast: (toast) => {
    // Deduplication: skip if a toast with the same message was added within 3 seconds
    const now = Date.now();
    const lastShown = _recentToastMessages.get(toast.message);
    if (lastShown && now - lastShown < TOAST_DEDUP_WINDOW_MS) {
      return;
    }
    _recentToastMessages.set(toast.message, now);

    const id = generateId('toast');
    const newToast: Toast = {
      id,
      duration: DEFAULT_TOAST_DURATION,
      ...toast,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },

  // Error actions
  addError: (error) => {
    const id = generateId('error');
    const newError: AppError = {
      id,
      timestamp: new Date(),
      ...error,
    };

    set((state) => ({
      errors: [...state.errors, newError],
    }));
  },

  removeError: (id) => {
    set((state) => ({
      errors: state.errors.filter((error) => error.id !== id),
    }));
  },

  clearErrors: () => {
    set({ errors: [] });
  },

  // Convenience methods
  showSuccess: (message, title = 'Success', duration = DEFAULT_TOAST_DURATION) => {
    set((state) => {
      const id = generateId('toast');
      return {
        toasts: [
          ...state.toasts,
          {
            id,
            type: 'success' as ToastType,
            title,
            message,
            duration,
          },
        ],
      };
    });
  },

  showError: (message, title = 'Error', duration = 0) => {
    // Errors don't auto-dismiss by default
    set((state) => {
      const id = generateId('toast');
      return {
        toasts: [
          ...state.toasts,
          {
            id,
            type: 'error' as ToastType,
            title,
            message,
            duration,
          },
        ],
      };
    });
  },

  showWarning: (message, title = 'Warning', duration = DEFAULT_TOAST_DURATION) => {
    set((state) => {
      const id = generateId('toast');
      return {
        toasts: [
          ...state.toasts,
          {
            id,
            type: 'warning' as ToastType,
            title,
            message,
            duration,
          },
        ],
      };
    });
  },

  showInfo: (message, title = 'Info', duration = DEFAULT_TOAST_DURATION) => {
    set((state) => {
      const id = generateId('toast');
      return {
        toasts: [
          ...state.toasts,
          {
            id,
            type: 'info' as ToastType,
            title,
            message,
            duration,
          },
        ],
      };
    });
  },
}));
